import { BEM } from '@feedfm/feed-util';
import React from 'react';
import { Alert, Button, Table } from 'react-bootstrap';
import { Widget } from '~/components/Widget/Widget';
import { ContentSourceDetailsFragment } from '~/graphqlTypes/ContentSourceDetailsFragment';
import { ContentSourceRightsMappingFragment } from '~/graphqlTypes/ContentSourceRightsMappingFragment';
import { _ContentSourceRightsMappingModal } from './_ContentSourceRightsMappingModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faX } from '@fortawesome/free-solid-svg-icons';

import './_ContentSourceDetailRightsMappings.scss';
import { useRightsMappingModal } from '~/components/ContentSources/_ContentSourceDetail/useRightsMappingModal';

// I considered putting this somewhere global, but it's only referenced by this module and the
// modal, at the moment, and we don't have a great location for client data layer.
export const useTypes = [
  { value: '*', label: 'Any' },
  { value: 'AsPerContract', label: 'AsPerContract' },
  { value: 'ConditionalDownload', label: 'ConditionalDownload' },
  { value: 'NonInteractiveStream', label: 'NonInteractiveStream' },
  { value: 'OnDemand', label: 'OnDemand' },
  { value: 'OnDemandStream', label: 'OnDemandStream' },
  { value: 'PermanentDownload', label: 'PermanentDownload' },
  { value: 'Stream', label: 'Stream' },
  { value: 'UseAsRingtone', label: 'UseAsRingtone' },
];

export const commercialModelTypes = [
  { value: '*', label: 'Any' },
  { value: 'AsPerContract', label: 'AsPerContract' },
  { value: 'PayAsYouGoModel', label: 'PayAsYouGoModel' },
  { value: 'AdvertisementSupportedModel', label: 'AdvertisementSupportedModel' },
  { value: 'SubscriptionModel', label: 'SubscriptionModel' },
];

export interface ContentSourceDetailsRightsMappingsProps {
  source: ContentSourceDetailsFragment;
  handleUpdateRightsMapping: (
    newRightsMappings: ContentSourceRightsMappingFragment,
    original?: ContentSourceRightsMappingFragment,
  ) => Promise<void>;
  handleDeleteRightsMapping: ( right: ContentSourceRightsMappingFragment ) => Promise<void>;
}

export const ContentSourceDetailRightsMappings: React.FC<ContentSourceDetailsRightsMappingsProps> = ( props ) => {
  const { source, handleUpdateRightsMapping, handleDeleteRightsMapping } = props;
  const bem = new BEM( 'ops-catalog-sources-detail-rights-mappings', [] );
  const { showing, show, controls } = useRightsMappingModal( source );

  const createModel = () =>
    show( {
      isNew: true,
      initialModel: {},
      saveHandler: handleUpdateRightsMapping,
    } );

  const editModel = ( initialModel: ContentSourceRightsMappingFragment ) =>
    show( {
      isNew: false,
      initialModel,
      saveHandler: ( newMapping ) => handleUpdateRightsMapping( newMapping, initialModel ),
    } );

  const handleDelete = ( mapping: ContentSourceRightsMappingFragment ) => {
    if ( window.confirm( 'Are you sure you want to delete this streaming right? This cannot be undone.' ) ) {
      handleDeleteRightsMapping( mapping );
    }
  };

  const canCreateMore = source.rightsMappings.length < commercialModelTypes.length * useTypes.length;

  return (
    <section className={ BEM.$( bem ) } data-testid="ops-catalog-sources-detail-rights-mappings">
      <Widget title="Rights mappings" titleElement='h4'>
        <Table size="sm" striped>
          <thead>
            <tr>
              <th>Use Type</th>
              <th>Commercial Model Type</th>
              <th>Streaming right</th>
            </tr>
          </thead>
          <tbody>
            { source.rightsMappings.map( ( mapping, index ) => (
              <tr key={ index }>
                <td>{ mapping.useType === '*' ? 'Any' : mapping.useType }</td>
                <td>{ mapping.commercialModelType === '*' ? 'Any' : mapping.commercialModelType }</td>
                <td>{ mapping.streamingRights.map( ( { name } ) => name ).join( ', ' ) }</td>
                <td className={ BEM.$( bem.el( 'row-actions' ) ) }>
                  <Button onClick={ () => editModel( mapping ) } size="sm" className={ BEM.$( bem.el( 'action' ) ) }>
                    <FontAwesomeIcon icon={ faEdit } />
                  </Button>{ ' ' }
                  <Button
                    onClick={ () => handleDelete( mapping ) }
                    variant="danger"
                    size="sm"
                    className={ BEM.$( bem.el( 'action' ) ) }
                  >
                    <FontAwesomeIcon icon={ faX } />
                  </Button>
                </td>
              </tr>
            ) ) }
          </tbody>
        </Table>
        { !canCreateMore && <Alert variant="warning">All possible mappings have been created.</Alert> }
        { canCreateMore && <Button onClick={ createModel }>Create</Button> }
      </Widget>
      { showing && (
        <_ContentSourceRightsMappingModal
          modalControls={ controls }
          source={ source }
          editModel={ editModel }
        />
      ) }
    </section>
  );
};
